((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.DreamItJourneyRoutes = api;
    const localeElement = root.document.getElementById?.('dim-journey-locale');
    if (localeElement) {
      const { localeRoot } = JSON.parse(localeElement.textContent);
      api.configureLocale({ localeRoot, allowedRoots: localeRoot === '/' ? ['/'] : ['/', localeRoot] });
    }
    const initialise = () => {
      api.enhanceSourceLinks(root.document, root.location);
      api.enhanceProgress(root.document, root);
      api.enhanceJourneyTransitions(root.document, root);
    };
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', initialise, { once: true });
    else initialise();
    root.document.addEventListener('shopify:section:load', initialise);
    root.addEventListener('pageshow', initialise);
    root.matchMedia?.('(max-width: 600px)').addEventListener?.('change', () => api.enhanceProgress(root.document, root));
  }
})(typeof window !== 'undefined' ? window : globalThis, () => {
  'use strict';
  const localeRoot = value => {
    if (value === '/') return '/';
    if (typeof value !== 'string' || !/^\/[a-z]{2}(?:-[a-z]{2})?\/?$/.test(value)) throw new Error('Invalid locale root.');
    return value.endsWith('/') ? value.slice(0, -1) : value;
  };
  let locale = Object.freeze({ root: '/', allowed: Object.freeze(['/']) });
  const configureLocale = ({ localeRoot: current, allowedRoots } = {}) => {
    const allowed = (Array.isArray(allowedRoots) && allowedRoots.length ? allowedRoots : [current]).map(localeRoot);
    if (new Set(allowed).size !== allowed.length) throw new Error('Invalid locale root.');
    const root = localeRoot(current);
    if (!allowed.includes(root)) throw new Error('Invalid locale root.');
    locale = Object.freeze({ root, allowed: Object.freeze([...allowed]) });
    return locale;
  };
  const localPath = pathname => {
    if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
    const localized = locale.allowed.filter(root => root !== '/').sort((left, right) => right.length - left.length)
      .find(root => pathname === root || pathname.startsWith(`${root}/`));
    return localized ? pathname.slice(localized.length) || '/' : pathname;
  };
  const localePath = pathname => locale.root === '/' ? pathname : `${locale.root}${pathname}`;
  const continuationUrl = () => localePath('/pages/dream-it');
  const safeTarget = ({ url, origin }) => {
    const target = new URL(url, origin);
    if (target.origin !== origin || target.username || target.password || /%2f|%5c/i.test(target.pathname)) throw new Error('Invalid product preview destination.');
    return target;
  };
  const productPath = target => {
    const path = localPath(target.pathname);
    return path && /^\/products\/[^/]+\/?$/.test(path) ? path.replace(/\/$/, '') : null;
  };
  const sourcePath = target => {
    const path = localPath(target.pathname);
    return path && /^\/(?:products\/[^/]+|collections\/all|pages\/dream-it)\/?$/.test(path) ? path.replace(/\/$/, '') : null;
  };
  const reviewUrl = ({ productUrl, variantId, route, origin, family }) => {
    if (!['upload', 'create'].includes(route) || !/^[1-9][0-9]*$/.test(String(variantId))) throw new Error('Invalid product preview selection.');
    const target = safeTarget({ url: productUrl, origin });
    const path = productPath(target);
    if (!path) throw new Error('Invalid product preview destination.');
    target.search = '';
    target.hash = '';
    target.searchParams.set('view', 'review');
    target.searchParams.set('route', route);
    target.searchParams.set('variant', String(variantId));
    target.searchParams.set('@dim_source', 'own-image');
    if (families.includes(family)) target.searchParams.set('journey', family);
    return `${localePath(path)}${target.search}`;
  };
  const sourceHints = (search) => {
    if (typeof search !== 'string') return null;
    const params = new URLSearchParams(search);
    const entries = params.getAll('entry');
    const routes = params.getAll('route');
    if (entries.length !== 1 || entries[0] !== 'source' || routes.length > 1
      || (routes.length === 1 && !['upload', 'create'].includes(routes[0]))) return null;
    return Object.freeze({ entry: 'source', route: routes[0] || '' });
  };
  const sourceUrl = ({ url, search, origin }) => {
    const hints = sourceHints(search);
    if (!hints) return null;
    try {
      const target = safeTarget({ url, origin });
      const path = sourcePath(target);
      if (!path) return null;
      target.pathname = localePath(path);
      target.searchParams.set('entry', 'source');
      target.searchParams.delete('route');
      if (hints.route) target.searchParams.set('route', hints.route);
      return `${target.pathname}${target.search}${target.hash}`;
    } catch (_) { return null; }
  };
  const journeyTarget = ({ url, origin }) => {
    try {
      const target = safeTarget({ url, origin });
      if (!sourcePath(target)) throw new Error('Invalid journey destination.');
      return target;
    } catch (_) { return null; }
  };
  // Presentation metadata only. These values confer no admission, artwork or
  // completion authority and never enter the closed scene/operation schemas.
  const families = ['guided', 'product', 'source'];
  const routeFamily = ({ search = '', designOrigin, sourceMode = false } = {}) => {
    if (designOrigin === 'guided') return 'guided'; // caller has validated its current draft
    const params = new URLSearchParams(search);
    if (params.getAll('journey').length > 1) return 'product';
    if (families.includes(params.get('journey'))) return params.get('journey');
    if (params.getAll('view').length === 1 && params.get('view') === 'dim-discovery'
      || params.getAll('guided').length === 1 && params.get('guided') === '1') return 'guided';
    return sourceMode || sourceHints(search) ? 'source' : 'product';
  };
  const presentationUrl = ({ url, origin, search = '', family } = {}) => {
    const target = journeyTarget({ url, origin });
    if (!target) return null;
    // Explicit staff selectors have a closed authenticated URL contract.
    if (['storefrontId', 'revision', 'bindingId'].some(key => target.searchParams.has(key))) return `${target.pathname}${target.search}${target.hash}`;
    const targetHint = target.searchParams.getAll('journey');
    let chosen = families.includes(family) ? family : targetHint.length === 1 && families.includes(targetHint[0])
      ? targetHint[0] : routeFamily({ search: search || target.search });
    // Choose can continue a collected source. Preserve that established operation
    // instead of labelling it product-first while retaining entry=source.
    if (chosen === 'product' && sourceHints(target.search)) chosen = 'source';
    target.searchParams.set('journey', chosen);
    return `${target.pathname}${target.search}${target.hash}`;
  };
  const progressState = ({ family = 'product', currentStep = 2, completed = [] } = {}) => {
    family = families.includes(family) ? family : 'product';
    const stages = family === 'guided' ? [1, 2, 3, 4, 5] : family === 'source' ? [3, 2, 4, 5] : [2, 3, 4, 5];
    currentStep = stages.includes(Number(currentStep)) ? Number(currentStep) : stages[0];
    return { family, stages, currentStep, ordinal: stages.indexOf(currentStep) + 1, total: stages.length,
      completed: [...new Set(completed)].filter(stage => stages.includes(stage) && stage !== currentStep) };
  };
  const progressLayouts = new WeakMap();
  const projectProgress = (navigation, options = {}) => {
    if (!navigation) return null;
    const state = progressState(options);
    navigation.dataset.journeyFamily = state.family;
    navigation.dataset.journeyStep = String(state.currentStep);
    navigation.dataset.journeyOrdinal = String(state.ordinal);
    navigation.style?.setProperty('--journey-stage-count', String(state.total));
    const items = Array.from(navigation.querySelectorAll('[data-journey-stage]'));
    const list = navigation.querySelector?.('ol');
    let label = '';
    items.forEach(item => {
      const stage = Number(item.dataset.journeyStage);
      const ordinal = state.stages.indexOf(stage) + 1;
      item.hidden = !ordinal;
      const current = stage === state.currentStep;
      if (current) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      if (state.completed.includes(stage)) item.setAttribute('data-journey-complete', ''); else item.removeAttribute('data-journey-complete');
      const number = item.querySelector('[data-journey-number]');
      if (number) number.textContent = String(ordinal || '');
      const status = item.querySelector('[data-journey-status-copy]');
      if (status) status.textContent = ` · ${current ? status.dataset.currentCopy : state.completed.includes(stage) ? status.dataset.completeCopy : status.dataset.pendingCopy}`;
      if (current) label = item.dataset.journeyLabel || item.querySelector('[data-journey-label]')?.textContent || '';
    });
    // DOM order, rather than CSS order, keeps the mobile disclosure readable.
    const ordered = state.stages.map(stage => items.find(node => Number(node.dataset.journeyStage) === stage)).filter(Boolean);
    if (list?.appendChild && ordered.some((item, index) => Array.from(list.children).filter(node => !node.hidden)[index] !== item)) ordered.forEach(item => list.appendChild(item));
    const count = navigation.querySelector?.('[data-journey-count]');
    if (count) count.textContent = (navigation.dataset.countCopy || 'Step [current] of [total]').replace('[current]', String(state.ordinal)).replace('[total]', String(state.total));
    const currentLabel = navigation.querySelector?.('[data-journey-current-label]');
    if (currentLabel) currentLabel.textContent = label;
    const details = navigation.querySelector?.('[data-journey-details]');
    const compact = typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(max-width: 600px)').matches;
    if (details && progressLayouts.get(navigation) !== compact) { details.open = !compact; progressLayouts.set(navigation, compact); }
    return state;
  };
  const enhanceProgress = (document, window) => {
    document.querySelectorAll?.('[data-journey-progress]').forEach(nav => {
      const completed = nav.dataset.journeyCompleted ? nav.dataset.journeyCompleted.split(',').map(Number)
        : Array.from(nav.querySelectorAll('[data-journey-complete]')).map(item => Number(item.dataset.journeyStage));
      projectProgress(nav, { family: nav.dataset.journeyOwner === 'guided' || nav.dataset.journeyRestoredFamily === 'guided' ? 'guided' : nav.hasAttribute?.('data-ux-stage-navigation') && nav.dataset.journeyFamily || routeFamily({ search: window.location.search }), currentStep: Number(nav.dataset.journeyStep), completed });
    });
    document.querySelectorAll?.('a[data-dim-journey-transition], a[data-change-product], a[data-product-edit-image]').forEach(link => {
      const href = presentationUrl({ url: link.getAttribute('href'), origin: window.location.origin, search: window.location.search, family: link.dataset.journeyEntry });
      if (href) link.setAttribute('href', href);
    });
  };
  const continuationRequired = runtime => {
    if (runtime?.enabled !== true) return false;
    if (runtime?.error) return true;
    // A destination may initially expose its primary shell while carrying a
    // campaign continuation. The provisional context is only a routing hint;
    // prepareTransition always verifies it with the server before navigation.
    const context = runtime.continuation?.provisionalContext || runtime.context;
    return context?.mode === 'campaign' || context?.mode === 'preview';
  };
  const navigateJourney = async ({ url, runtime, location, assign, isCurrent, family } = {}) => {
    const target = journeyTarget({ url, origin: location?.origin });
    if (!target) throw new Error('INVALID_JOURNEY_DESTINATION');
    if (typeof isCurrent === 'function' && !isCurrent()) throw new Error('JOURNEY_STALE');
    const destination = location?.search || families.includes(family)
      ? presentationUrl({ url: target.href, origin: location.origin, search: location.search, family })
      : `${target.pathname}${target.search}${target.hash}`;
    if (continuationRequired(runtime)) {
      if (typeof runtime?.prepareTransition !== 'function') throw new Error('JOURNEY_TRANSITION_UNAVAILABLE');
      await runtime.prepareTransition(target.pathname);
      if (typeof isCurrent === 'function' && !isCurrent()) throw new Error('JOURNEY_STALE');
    }
    if (typeof assign !== 'function') throw new Error('JOURNEY_NAVIGATION_UNAVAILABLE');
    assign(destination);
    return destination;
  };
  const errorCode = error => /^[A-Z][A-Z0-9_]{1,79}$/.test(error?.code || error?.message || '')
    ? (error.code || error.message) : 'JOURNEY_TRANSITION_FAILED';
  const transitionStates = new WeakMap();
  const transitionRecoveries = new WeakMap();
  let transitionRecoveryId = 0;
  const visibleAuthenticationNotice = (document, code) => {
    if (!['DEV_SIGN_IN_REQUIRED', 'DEV_SESSION_REQUIRED', 'DEV_SESSION_INVALID', 'DEV_SESSION_EXPIRED'].includes(code)) return false;
    const notice = document.querySelector?.('[data-dim-dev-session-notice]');
    return Boolean(notice && notice.hidden === false);
  };
  const showTransitionRecovery = (document, link, code) => {
    if (visibleAuthenticationNotice(document, code)) return;
    let status = transitionRecoveries.get(link);
    if (!status) {
      if (!document.createElement) return;
      status = document.createElement('p');
      status.id = `dim-journey-transition-status-${++transitionRecoveryId}`;
      status.dataset.dimJourneyTransitionStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const describedBy = (link.getAttribute?.('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!describedBy.includes(status.id)) link.setAttribute?.('aria-describedby', [...describedBy, status.id].join(' '));
      if (typeof link.insertAdjacentElement === 'function') link.insertAdjacentElement('afterend', status);
      else if (link.parentNode?.insertBefore) link.parentNode.insertBefore(status, link.nextSibling || null);
      else return;
      transitionRecoveries.set(link, status);
    }
    // Reuse the established product-choice failure wording for the bounded
    // fallback. A mounted development-session notice remains the recovery
    // surface for authentication failures and is only opened by its button.
    status.textContent = code === 'JOURNEY_SAVE_FAILED'
      ? 'Your latest changes are not saved yet. Stay on this page to finish or recover them, then try again.'
      : 'This journey is unavailable right now. Please try again, or browse the products.';
  };
  const isJourneyActivation = (event, link) => Boolean(event && link && !event.defaultPrevented
    && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    && (!link.target || link.target.toLowerCase() === '_self') && !link.hasAttribute?.('download'));
  // Task owners flush their existing stores. Routing owns no draft or paid state.
  const prepareDeparture = async (document, window) => {
    if (!document?.dispatchEvent || !window?.CustomEvent) return;
    const saves = [];
    const event = new window.CustomEvent('dim:before-journey-leave', { cancelable: true,
      detail: { waitUntil: value => saves.push(Promise.resolve(value)) } });
    const allowed = document.dispatchEvent(event);
    const results = await Promise.all(saves);
    if (allowed === false || results.some(value => value === false)) throw new Error('JOURNEY_SAVE_FAILED');
  };
  const enhanceJourneyTransitions = (document, window) => {
    if (!document?.addEventListener || !window?.location || document.__dimJourneyTransitionBound) return;
    Object.defineProperty(document, '__dimJourneyTransitionBound', { value: true, configurable: true });
    let departureRevision = 0;
    document.addEventListener('click', event => {
      const link = event.target?.closest?.('a[data-dim-journey-transition]')
        || event.target?.closest?.('.dim-header a[href], a[data-dim-preserve-work]');
      if (!link || event.defaultPrevented) return;
      if (link.hasAttribute?.('data-e-help-open') || link.hasAttribute?.('data-e-editor-help')) return;
      // A task-owned disabled action must stop before continuation capture,
      // including modified clicks that would otherwise leave the current task.
      if (link.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      // A modified click or target that opens another browsing context is a
      // direct, independent entry. Do not copy a continuation into it.
      if (!isJourneyActivation(event, link)) return;
      const runtime = window.DIMRuntime;
      const headerDeparture = Boolean(link.closest?.('.dim-header') || link.hasAttribute?.('data-dim-preserve-work'));
      if (!headerDeparture && !continuationRequired(runtime)) return;
      const href = link.getAttribute('href');
      let destination;
      try { destination = safeTarget({ url: href, origin: window.location.origin }); } catch (_) {}
      const journey = journeyTarget({ url: href, origin: window.location.origin });
      if (!destination || !headerDeparture && !journey) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const previous = transitionStates.get(link);
      if (previous?.pending) return;
      const departure = headerDeparture ? ++departureRevision : null;
      const state = { pending: true, revision: (previous?.revision || 0) + 1, href };
      transitionStates.set(link, state);
      link.setAttribute('aria-busy', 'true');
      const current = () => transitionStates.get(link) === state && link.isConnected !== false && link.getAttribute('href') === href
        && (!headerDeparture || departure === departureRevision);
      void (async () => {
        if (headerDeparture) await prepareDeparture(document, window);
        if (link.hasAttribute?.('data-header-continue') && !await window.DIMSourceIntake?.describeContinuation?.()) {
          throw new Error('JOURNEY_CONTINUATION_UNAVAILABLE');
        }
        if (!current()) throw new Error('JOURNEY_STALE');
        let navigationHref = href;
        if (headerDeparture && link.hasAttribute?.('data-journey-explicit')
          && destination.searchParams.get('entry') === 'source'
          && ['upload', 'create'].includes(destination.searchParams.get('route')) && window.DIMSourceIntake?.selectSource) {
          const selected = window.DIMSourceIntake.selectSource(destination.searchParams.get('route'));
          if (selected.status === 'pending') navigationHref = continuationUrl();
        }
        if (journey) return navigateJourney({ url: navigationHref, runtime, location: window.location, assign: window.location.assign.bind(window.location), isCurrent: current,
          family: link.hasAttribute?.('data-journey-explicit') ? link.dataset.journeyEntry : undefined });
        if (continuationRequired(runtime)) {
          if (!runtime?.prepareTransition) throw new Error('JOURNEY_TRANSITION_UNAVAILABLE');
          await runtime.prepareTransition(destination.pathname);
          if (!current()) throw new Error('JOURNEY_STALE');
        }
        window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
      })()
        .catch(error => {
          if (!current()) return;
          link.dataset.dimJourneyError = 'true';
          showTransitionRecovery(document, link, errorCode(error));
          link.focus?.({ preventScroll: true });
        })
        .finally(() => {
          state.pending = false;
          if (transitionStates.get(link) === state) link.removeAttribute('aria-busy');
        });
    }, true);
  };
  const enhanceSourceLinks = (document, location) => {
    if (!document || !location || !sourceHints(location.search)) return;
    document.querySelectorAll('a[data-source-link], a[data-change-product]').forEach((link) => {
      if (link.hasAttribute?.('data-journey-explicit') || link.dataset?.journeyExplicit !== undefined) return;
      const destination = sourceUrl({ url: link.getAttribute('href'), search: location.search, origin: location.origin });
      if (destination) link.setAttribute('href', destination);
    });
    document.querySelectorAll('[data-source-heading], [data-source-note]').forEach((element) => {
      if (element.dataset.sourceCopy) element.textContent = element.dataset.sourceCopy;
    });
  };
  return Object.freeze({ configureLocale, continuationUrl, prepareDeparture, reviewUrl, sourceHints, sourceUrl, navigateJourney, isJourneyActivation, enhanceSourceLinks, enhanceJourneyTransitions,
    routeFamily, presentationUrl, progressState, projectProgress, enhanceProgress });
});
